import { Alert, Button, Col, Descriptions, Row, Tag, Typography } from 'antd';
import type { ShipiumTagConfig } from '../../components/shared/ShipiumTag';
import { ShipiumTag, ShipiumTagWithConfig } from '../../components/shared/ShipiumTag';
import type { SimulationDetail, SimulationType } from '../../store/simulation/types';
import { SimulationTypeToDisplay, safeDateFormat } from './utils';

import { FileOutlined } from '@ant-design/icons';
import { SimulationCard } from './SimulationCard';
import { SimulationSellRateSheetSummary } from '../../components/simulation/simulationReporting/SimulationSellRateSheetSummary';
import { SimulationTimeline } from './SimulationTimeline';
import axios from 'axios';
import getGatewayUri from '../../store/services/getGatewayUri';
import { useNavigate } from 'react-router-dom-v5-compat';
import { useStoreState } from '../../store';
import {
  historicalShipmentFilterDetails,
  useOriginMap,
  useTenantMap,
} from '~/components/simulation/shared/SimulationSummaryDetails/HistoricalShipmentFilterDetails';
import { TNT_MODEL_SOURCE_MAPPING, TNT_SETTING_MAPPING } from '~/store/simulation/types';
import { useSession } from '~/hooks/useSession';
import { LIMIT_BEHAVIOR_LABEL } from '~/store/limit/utils';
import { toPresignedDownloadSubKey } from '~/components/simulation/shared/uploadAdapter';

const { Item } = Descriptions;
const { Text } = Typography;

export interface SimulationDetailTabProps {
  simulation: SimulationDetail;
}

export const SimulationDetailTab = ({ simulation }: SimulationDetailTabProps) => {
  const sessionPartnerSlug = useStoreState((state) => state?.session?.user?.sessionPartnerUrl);
  const partnerConfig = useStoreState((state) => state?.partner?.activePartner);
  const { isShipiumView } = useSession();
  const simulationStatus =
    simulation.totalShipmentCount === simulation.completedShipmentCount && simulation.simulationStatus === 'processing'
      ? 'finalizing'
      : simulation.simulationStatus;
  const TrueTag = <ShipiumTag color="Teal">True</ShipiumTag>;
  const FalseTag = <ShipiumTag color="Coral">False</ShipiumTag>;

  const shipiumTagMapStatus: Record<string, ShipiumTagConfig> = {
    configuring: { color: 'Fuschia', label: 'Configuring', dark: true, icon: 'config' },
    processing: { color: 'Blue', label: 'Processing', dark: true, icon: 'processing' },
    finalizing: { color: 'Teal', label: 'Finalizing', dark: true, icon: 'processing' },
    completed: { color: 'Teal', label: 'Complete', dark: true, icon: 'check' },
    exception: { color: 'Coral', label: 'Exception', dark: true, icon: 'exclamation' },
    cancelled: { color: 'Coral', label: 'Cancelled', dark: true, icon: 'stop' },
    archived: { color: 'Grey', label: 'Archived', dark: true, icon: 'delete' },
  };

  const shipiumTagMapExecutionPriority: Record<string, ShipiumTagConfig> = {
    low: { color: 'Grey', label: 'Low' },
    standard: { color: 'Grey', label: 'Standard' },
    high: { color: 'Gold', label: 'High' },
  };

  const SimulationTypeTag = (simulationType: SimulationType) => {
    return <ShipiumTag color={'Grey'}>{SimulationTypeToDisplay[simulationType]}</ShipiumTag>;
  };

  const isArchived = simulation.simulationStatus === 'archived';
  const isAdvancedSim = simulation.simulationType === 'advanced';
  const navigate = useNavigate();

  const { simulationShipmentOptions } = simulation;
  const {
    costAdjustLimitEnabled,
    costAdjustLimitTargetCarrier,
    costAdjustLimitExcludedCarriers,
    costAdjustLimitTargetPercent,
    costAdjustLimitSeedAdjustment,
    costAdjustLimitTargetBehavior,
    tenantIds = [],
    originIds = [],
  } = simulationShipmentOptions ?? {};

  const { data: tenantMap } = useTenantMap({ enabled: !!tenantIds?.length });
  const { data: originMap } = useOriginMap({ enabled: !!originIds?.length });

  return (
    <Row gutter={16}>
      <Col span={17}>
        {isArchived && (
          <Alert
            style={{ marginBottom: 10 }}
            type="warning"
            showIcon
            message={`This simulation has been archived`}
            description={'Simulations which have been archived have no shipment or reporting data.'}
          />
        )}
        <SimulationCard
          heading={'Simulation Details'}
          collapsable={false}
          collapsed={false}
          archived={isArchived}
          extra={
            isShipiumView ? (
              <ShipiumTag dark={true} color={simulation.visibleToPartner ? 'Teal' : 'Coral'}>
                {simulation.visibleToPartner ? 'Visible to Partner' : 'Hidden from Partner'}
              </ShipiumTag>
            ) : null
          }
          editAction={
            simulation.simulationStatus === 'configuring'
              ? () => {
                  navigate(`/${sessionPartnerSlug}/simulation/${simulation.simulationId}/edit/simulation`);
                }
              : undefined
          }
        >
          <Descriptions layout={'vertical'} column={2} size="middle" contentStyle={{ fontWeight: 'bold' }}>
            <Item label={'Name'}>{simulation.name}</Item>
            <Item label={'Description'}>{simulation.description}</Item>
            <Item label={'Status'}>
              <ShipiumTagWithConfig configMap={shipiumTagMapStatus} tag={simulationStatus} />
            </Item>
            <Item label={'Simulation Type'}>{SimulationTypeTag(simulation.simulationType)}</Item>
            <Item label={'Execution Priority'}>
              <ShipiumTagWithConfig configMap={shipiumTagMapExecutionPriority} tag={simulation.executionPriority} />
            </Item>
            <Item label={'Includes Historical Selection'}>
              <ShipiumTag dark={true} color={simulation.includesHistoricalSelection ? 'Teal' : 'Coral'}>
                {simulation.includesHistoricalSelection ? 'true' : 'false'}
              </ShipiumTag>
            </Item>
            {historicalShipmentFilterDetails({
              assetFilters: {
                tenants: tenantIds.map((id) => tenantMap?.[id]?.name ?? id),
                origins: originIds.map((id) => originMap?.[id]?.name ?? id),
              },
            })}
            {simulation.simulationShipmentOptions?.ignoreRuleSets && (
              <>
                <Item>&nbsp;</Item>
                <Item label={'Ignores Rule Sets'}>
                  <ShipiumTag
                    dark={true}
                    color={simulation.simulationShipmentOptions?.ignoreRuleSets ? 'Teal' : 'Coral'}
                  >
                    {simulation.simulationShipmentOptions?.ignoreRuleSets ? 'true' : 'false'}
                  </ShipiumTag>
                </Item>
              </>
            )}
            {simulation.simulationShipmentOptions?.carrierServiceMethodExclusionList?.length ? (
              <Item label="Excluded Service Methods">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {simulation.simulationShipmentOptions?.carrierServiceMethodExclusionList.map((item) => (
                    <ShipiumTag key={item} color="Coral">
                      {item}
                    </ShipiumTag>
                  ))}
                </div>
              </Item>
            ) : (
              !!simulation.simulationShipmentOptions?.carrierServiceMethodAllowList?.length && (
                <Item label="Allowed Service Methods">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {simulation.simulationShipmentOptions?.carrierServiceMethodAllowList.map((item) => (
                      <ShipiumTag key={item} color="Teal">
                        {item}
                      </ShipiumTag>
                    ))}
                  </div>
                </Item>
              )
            )}
            {simulation.simulationShipmentOptions?.ignoreCarrierServiceMethodAllowList && (
              <>
                <Item>&nbsp;</Item>
                <Item label={'Ignores Carrier Service Method Allow List'}>
                  <ShipiumTag
                    dark={true}
                    color={simulation.simulationShipmentOptions?.ignoreCarrierServiceMethodAllowList ? 'Teal' : 'Coral'}
                  >
                    {simulation.simulationShipmentOptions?.ignoreCarrierServiceMethodAllowList ? 'true' : 'false'}
                  </ShipiumTag>
                </Item>
              </>
            )}
            <Item label={'Shiptime Configuration Version'}>
              {<Text>Version {simulation.simulationShipmentOptions?.shiptimeVersion || '2'}</Text>}
            </Item>
            <Item label={'Sampling Percentage'}>
              {safeDateFormat(simulation.samplingPercentage ? simulation.samplingPercentage.toString() : '100')}%
            </Item>
            <Item label={'Source Date Start'}>{safeDateFormat(simulation.simulationSourceStartDate)}</Item>
            <Item label={'Source Date End'}>{safeDateFormat(simulation.simulationSourceEndDate)}</Item>
          </Descriptions>
          {
            // it ends up not being super easy to
            // display if there multiple files
            // since there is mostly only one file
            // I am including this half measure
          }
          {!!simulation?.historicalShipmentS3Locations && simulation?.historicalShipmentS3Locations?.length === 1 && (
            <Item label={'View uploaded historical shipment file'}>
              <Button
                onClick={() => {
                  axios({
                    method: 'POST',
                    url: `${getGatewayUri()}/getPresignedDownloadUrl`,
                    data: {
                      subKey: toPresignedDownloadSubKey(simulation?.historicalShipmentS3Locations?.[0]),
                    },
                    withCredentials: true,
                  }).then((val) => {
                    const link = document.createElement('a');
                    link.setAttribute('href', val.data.downloadUrl);
                    link.setAttribute('download', `${simulation.name}_shipments`);
                    document.body.appendChild(link);
                    link.click();
                  });
                }}
                type="primary"
              >
                Download <FileOutlined />
              </Button>
            </Item>
          )}
        </SimulationCard>
        {simulation.simulationType === 'sell_rate_simulation' && (
          <SimulationCard
            heading="Simulation Sell Rate Sheets"
            collapsable={false}
            collapsed={false}
            archived={isArchived}
          >
            <SimulationSellRateSheetSummary simulationId={simulation.simulationId} />
          </SimulationCard>
        )}
        <SimulationCard
          heading={'Simulation Overrides'}
          collapsable={false}
          collapsed={false}
          archived={isArchived}
          editAction={
            simulation.simulationStatus === 'configuring'
              ? () => {
                  navigate(`/${sessionPartnerSlug}/simulation/${simulation.simulationId}/edit/simulation`);
                }
              : undefined
          }
        >
          <Descriptions layout={'vertical'} column={2} size="middle" contentStyle={{ fontWeight: 'bold' }}>
            <Item label={'Production Time In Transit Source Preferences'}>
              {partnerConfig?.deliveryExperienceConfigurationInfo?.timeInTransitSourcePreference
                ?.filter((f) => f !== 'carrier_default')
                .join(', ') || 'None'}
            </Item>
            <Item label={'Simulation Override'}>
              {simulation.simulationTntOptions?.timeInTransitSourcePreference &&
              simulation.simulationTntOptions?.timeInTransitSourcePreference?.length > 0 &&
              JSON.stringify(simulation.simulationTntOptions?.timeInTransitSourcePreference) !==
                JSON.stringify(partnerConfig?.deliveryExperienceConfigurationInfo?.timeInTransitSourcePreference) ? (
                simulation.simulationTntOptions?.timeInTransitSourcePreference?.join(', ')
              ) : (
                <Tag>Use production settings</Tag>
              )}
            </Item>

            {isAdvancedSim && (
              <>
                <Item label={'Production TNT Model Source'}>
                  {
                    TNT_MODEL_SOURCE_MAPPING[
                      partnerConfig?.deliveryExperienceConfigurationInfo?.timeInTransitModelSource ??
                        'shipment_delivery_prediction'
                    ]
                  }
                </Item>
                <Item label={'Simulation Override'}>
                  {simulation.simulationTntOptions?.timeInTransitModelSource ? (
                    TNT_MODEL_SOURCE_MAPPING[simulation.simulationTntOptions.timeInTransitModelSource]
                  ) : (
                    <Tag>Use production settings</Tag>
                  )}
                </Item>
                <Item label={'Production TNT Setting'}>
                  {
                    TNT_SETTING_MAPPING[
                      partnerConfig?.deliveryExperienceConfigurationInfo?.timeInTransitRoundingStrategy ??
                        'conservative'
                    ]
                  }
                </Item>
                <Item label={'Simulation Override'}>
                  {simulation.simulationTntOptions?.timeInTransitSetting ? (
                    TNT_SETTING_MAPPING[simulation.simulationTntOptions?.timeInTransitSetting]
                  ) : (
                    <Tag>Use production settings</Tag>
                  )}
                </Item>
              </>
            )}

            <Item label={'Production Additional Days to Process'}>
              {partnerConfig?.deliveryExperienceConfigurationInfo?.additionalDaysToProcess}
            </Item>
            <Item label={'Simulation Override'}>
              <Tag>Use production settings</Tag>
            </Item>

            <Item label={'Production Weekend Considered Transit Day'}>
              {partnerConfig?.deliveryExperienceConfigurationInfo?.weekendConsideredTransitDay ? 'Yes' : 'No'}
            </Item>
            <Item label={'Simulation Override'}>
              <Tag>Use production settings</Tag>
            </Item>

            <Item label={'Production Total Upgrade Cost Tolerance'}>
              {partnerConfig?.costingConfigurationInfo?.totalUpgradeCostTolerance || '-'}
            </Item>
            <Item label={'Simulation Override'}>
              <Tag>Use production settings</Tag>
            </Item>

            <Item label={'Production Upgrade Over Cheapest Cost Tolerance'}>
              {partnerConfig?.costingConfigurationInfo?.upgradeOverCheapestCostTolerance || '-'}
            </Item>
            <Item label={'Simulation Override'}>
              <Tag>Use production settings</Tag>
            </Item>
          </Descriptions>
        </SimulationCard>
        <SimulationCard
          heading={'Shipment Limits'}
          collapsable={false}
          collapsed={false}
          hidden={costAdjustLimitEnabled === undefined}
        >
          <Descriptions layout={'vertical'} column={2} size="middle" contentStyle={{ fontWeight: 'bold' }}>
            {costAdjustLimitEnabled !== undefined && (
              <Item label="Enabled">{costAdjustLimitEnabled ? TrueTag : FalseTag}</Item>
            )}
            {costAdjustLimitEnabled && costAdjustLimitTargetCarrier && (
              <Item label="Carrier">{costAdjustLimitTargetCarrier}</Item>
            )}
            {costAdjustLimitEnabled &&
              Array.isArray(costAdjustLimitExcludedCarriers) &&
              costAdjustLimitExcludedCarriers.length > 0 && (
                <Item label="Excluded Carriers">{costAdjustLimitExcludedCarriers?.join(', ')}</Item>
              )}
            {costAdjustLimitEnabled && costAdjustLimitTargetBehavior && (
              <Item label="Limit Behavior">{LIMIT_BEHAVIOR_LABEL[costAdjustLimitTargetBehavior]}</Item>
            )}
            {costAdjustLimitEnabled && costAdjustLimitTargetPercent !== undefined && (
              <Item label="Target Percent">{costAdjustLimitTargetPercent} %</Item>
            )}
            {costAdjustLimitEnabled && costAdjustLimitSeedAdjustment !== undefined && (
              <Item label="Fallback Cost Adjustment">{costAdjustLimitSeedAdjustment}</Item>
            )}
          </Descriptions>
        </SimulationCard>
      </Col>
      <Col span={7}>
        <SimulationTimeline title="Simulation Events" simulation={simulation} />
      </Col>
    </Row>
  );
};
